<script lang="ts">
// Based on MSAL redirect handling: https://github.com/AzureAD/microsoft-authentication-library-for-js
import { ref, onBeforeMount } from 'vue';
import { initializeMsal, useMsal } from '@/composables/useMsal';
import { useRouter, useRoute, LocationQueryValue } from 'vue-router';
import { useAccountStore } from '@/store/account/account.state';
import { useAnalyticsStore } from '@/store';
import { FormEvents } from '@/enums/form-events.enum';
import { CONTACT_JEOPARDY, HOME_PAGE } from '@/constants/urls.const';
import { SeverityLevel } from '@microsoft/applicationinsights-web';
import { useAppInsights } from '@/composables/useAppInsights';

function getRedirectPath(resultState: string | undefined, routeState: LocationQueryValue | LocationQueryValue[]) {
  if (typeof resultState === 'string' && resultState.length > 0) {
    const stateParts = resultState.split('|');
    return stateParts[stateParts.length - 1] || resultState;
  }

  if (Array.isArray(routeState)) {
    return routeState[0];
  }

  return routeState;
}

/**
 * Append eeid to url query param for avoka
 *
 * @param redirectPath
 * @param eeid
 */
function appendIdTokenToQuery(redirectPath: string, idToken: string): string {
  if (!redirectPath || !idToken || typeof window == 'undefined') {
    return redirectPath;
  }

  try {
    const isAbsolute = /^https?:\/\//i.test(redirectPath);
    const base = window?.location?.origin || HOME_PAGE;
    const redirectUrl = new URL(redirectPath, isAbsolute ? undefined : base);
    redirectUrl.searchParams.set('idToken', idToken);
    return isAbsolute ? redirectUrl.toString() : `${redirectUrl.pathname}${redirectUrl.search}${redirectUrl.hash}`;
  } catch {
    return redirectPath;
  }
}

export default {
  setup() {
    const error = ref<string | null>(null);
    const { instance } = useMsal();
    const route = useRoute();
    const store = useAccountStore();
    const analyticsStore = useAnalyticsStore();
    const router = useRouter();
    const appInsights = useAppInsights();

    onBeforeMount(async () => {
      try {
        await initializeMsal();

        const result = await instance.handleRedirectPromise();

        if (result) {
          await router.isReady();
          const idToken = result.idToken;
          if (idToken) {
            let redirectPath = getRedirectPath(result.state, route.query.state as LocationQueryValue);
            if (result.state?.includes('is_avoka')) {
              redirectPath = appendIdTokenToQuery(redirectPath as string, idToken);
            }
            // This needs to be after redirectPath is set, otherwise redirectPath is undefined
            await store.authenticateBackendSession(idToken);
            await store.fetchIdentityDetails();

            await analyticsStore.pushAnalytics({
              event: FormEvents.LOGIN_COMPLETE,
              form: {
                name: 'Login',
                stepName: 'Success',
              },
            });

            // Redirect back to previous page if we passed it
            if (redirectPath) {
              location.replace(redirectPath);
            }
          } else {
            console.error('[Auth] No id token from Entra ID, unable to authenticate with backend');
          }
        }
      } catch (e) {
        console.error('[Auth] LoginCallback error:', e);
        appInsights.trackException({
          exception: e instanceof Error ? e : new Error(String(e)),
          severityLevel: SeverityLevel.Error,
        });
        error.value = (e as Error).toString();
      }
    });
    return () => {
      if (error.value) {
        location.replace(CONTACT_JEOPARDY);
      }
    };
  },
};
</script>
